import { useQuery } from '@tanstack/react-query';
import type { TripDetail, TripSummary } from '@tappa/shared';
import { api } from './api.js';

const STORAGE_KEY = 'tappa.currentTripId';

export function getStoredTripId(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage non disponibile (modalita' privata su alcuni browser):
    // si degrada a "nessun viaggio selezionato", non si crasha.
    return null;
  }
}

export function setStoredTripId(tripId: string | null): void {
  try {
    if (tripId === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, tripId);
  } catch {
    // Ignorabile: la selezione tornera' al default al prossimo avvio.
  }
}

export function useTrips(): {
  trips: TripSummary[];
  isLoading: boolean;
  error: unknown;
} {
  const query = useQuery({
    queryKey: ['trips'],
    queryFn: () => api.trips.list(),
    staleTime: 30_000,
  });
  return {
    trips: query.data?.trips ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * Il viaggio "corrente": quello scelto esplicitamente, altrimenti il primo che
 * inizia nel futuro, altrimenti il piu' recente. Nessuna scelta arbitraria
 * nascosta: la regola e' esplicita e la UI mostra sempre quale viaggio e' attivo.
 */
export function pickCurrentTrip(
  trips: readonly TripSummary[],
  storedId: string | null,
  now: Date = new Date(),
): TripSummary | null {
  if (trips.length === 0) return null;

  if (storedId !== null) {
    const stored = trips.find((trip) => trip.id === storedId);
    if (stored !== undefined) return stored;
  }

  const upcoming = trips
    .filter((trip) => new Date(trip.endsAt).getTime() >= now.getTime())
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());

  return upcoming[0] ?? trips[0] ?? null;
}

export function useCurrentTrip(): {
  trip: TripSummary | null;
  detail: TripDetail | null;
  isLoading: boolean;
} {
  const { trips, isLoading } = useTrips();
  const selected = pickCurrentTrip(trips, getStoredTripId());

  const detailQuery = useQuery({
    queryKey: ['trip', selected?.id],
    queryFn: () => api.trips.detail(selected?.id ?? ''),
    enabled: selected !== null,
    staleTime: 30_000,
  });

  return {
    trip: selected,
    detail: detailQuery.data?.trip ?? null,
    isLoading: isLoading || detailQuery.isLoading,
  };
}
